// Get API URL from runtime config or fallback to build-time env or default
export const getApiBaseUrl = () => {
  // First, try to read from window.__ENV__ (injected at runtime)
  if (window.__ENV__ && window.__ENV__.REACT_APP_API_URL) {
    return window.__ENV__.REACT_APP_API_URL;
  }
  // Second, try to read from a config.js file (loaded in index.html)
  if (window.APP_CONFIG && window.APP_CONFIG.REACT_APP_API_URL) {
    return window.APP_CONFIG.REACT_APP_API_URL;
  }
  // Fallback to build-time environment variable or default
  return process.env.REACT_APP_API_URL || 'http://localhost:5050';
};

const API_BASE_URL = getApiBaseUrl();

const redirectToLoginAndClearToken = () => {
  try {
    localStorage.removeItem('token');
  } finally {
    // Full redirect ensures React state resets (UserContext, etc.)
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
};

// Global error handler for API responses
const handleApiResponse = async (response) => {
  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (e) {
      // If response is not JSON, create a generic error
      errorData = { message: `HTTP ${response.status}: ${response.statusText}` };
    }
    
    // If user is locked out, redirect to logout-on-lockout component
    if (response.status === 403 && errorData.message && errorData.message.includes('locked out')) {
      window.location.href = '/logout-on-lockout';
      return;
    }
    
    // Create more specific error messages based on status codes
    let errorMessage = errorData.error || errorData.message || 'Request failed';
    
    if (response.status === 401) {
      errorMessage = 'Invalid token';
      redirectToLoginAndClearToken();
    } else if (response.status === 403) {
      errorMessage = errorData.message || 'Access denied';
    } else if (response.status === 404) {
      errorMessage = 'User not found';
    } else if (response.status >= 500) {
      errorMessage = 'Server error - please try again later';
    }
    
    throw new Error(errorMessage);
  }
  
  return await response.json();
};

export const apiService = {
  // Generic GET method
  get: async (url) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // User registration
  registerUser: async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Registration failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get companies for registration dropdown
  getCompaniesForRegistration: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/companies`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch companies');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get users list
  getUsers: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get users by asset ID (company ID)
  getUsersByAssetId: async (assetId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/asset/${assetId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get user by ID
  getUserById: async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update user status (activate/deactivate)
  updateUserStatus: async (id, active) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ active })
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  updateUserLockoutStatus: async (id, isLockedOut) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${id}/lockout`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isLockedOut })
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (id, profileData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Upload or update current user's own profile (no id required)
  updateMyProfile: async (profileData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/auth/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update user password
  updateUserPassword: async (id, currentPassword, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Delete user
  deleteUser: async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // User login
  // User logout
  logout: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      // Even if logout fails on server, we still want to clear local storage
      // So we don't throw an error, just log it
      if (!response.ok) {
        console.warn('Logout API call failed, but clearing local storage anyway');
      }

      return await response.json().catch(() => ({ status: true, message: 'Logout successful' }));
    } catch (error) {
      // Don't throw error - we still want to clear local storage
      console.warn('Logout error:', error);
      return { status: true, message: 'Logout successful' };
    }
  },

  login: async (credentials) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Forgot password
  forgotPassword: async (email) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send password reset email');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Change password from profile
  changePasswordFromProfile: async (currentPassword, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/auth/user/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Get user profile
  getUserProfile: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/auth/user/profile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Company API functions
  getCompanies: async (page = 1, limit = 10) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      const response = await fetch(`${API_BASE_URL}/api/company?${params}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch companies');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  getCompanyById: async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/company/${id}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch company');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  searchCompanies: async (query, branch, state) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (branch) params.append('branch', branch);
      if (state) params.append('state', state);
      
      const response = await fetch(`${API_BASE_URL}/api/company/search?${params}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to search companies');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Projects API functions
  getProjectsByCompany: async (companyId, page = 1, limit = 25, type = undefined, status = undefined, search = undefined) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());
      if (type) params.append('type', type);
      if (status && status !== 'all') params.append('status', status);
      if (search && String(search).trim()) params.append('search', String(search).trim());
      
      const url = `${API_BASE_URL}/api/projects/company/${companyId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch projects');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  getProjectById: async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch project');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  updateProject: async (projectId, projectData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(projectData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to update project');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  getAllProjects: async (page = 1, limit = 25, type = undefined, status = undefined, search = undefined) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());
      if (type) params.append('type', type);
      if (status && status !== 'all') params.append('status', status);
      if (search && String(search).trim()) params.append('search', String(search).trim());
      
      const url = `${API_BASE_URL}/api/projects${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch projects');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get project releases
  getProjectReleases: async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/releases`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch project releases');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get project loads
  getProjectLoads: async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/loads`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch project loads');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get releases by load ID
  getReleasesByLoad: async (loadId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/load/${loadId}/releases`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch releases for load');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update release weight
  updateReleaseWeight: async (releaseId, weightData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/release/${releaseId}/weight`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(weightData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to update release weight');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get project sites
  getProjectSites: async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/sites`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch project sites');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Create project site
  createProjectSite: async (projectId, siteData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/sites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(siteData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to create project site');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update project site
  updateProjectSite: async (siteId, siteData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/projects/sites/${siteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(siteData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to update project site');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // SuperAdmin password reset
  resetUserPassword: async (userId, newPassword) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/super-admin/users/${userId}/reset-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword })
      });

      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Dashboard API functions - Optimized endpoints
  getDashboardMetrics: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/dashboard/metrics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getDashboardCharts: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/dashboard/charts`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getRecentActivity: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/dashboard/activity`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getProjectSummary: async (page = 1, limit = 5, status = 'all', companyId = 'all') => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      params.append('status', status);
      params.append('companyId', companyId);
      
      const response = await fetch(`${API_BASE_URL}/api/dashboard/projects/summary?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Project Reports API functions
  getCompaniesForReporting: async (page = 1, limit = 10, search = '') => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (search) params.append('search', search);
      
      const response = await fetch(`${API_BASE_URL}/api/project-reports/companies?${params}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch companies for reporting');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  getProjectsByCompanyForReporting: async (companyId, page = 1, limit = 10, search = '') => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (search) params.append('search', search);
      
      const response = await fetch(`${API_BASE_URL}/api/project-reports/companies/${companyId}/projects?${params}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch projects for reporting');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  generateProjectReport: async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/project-reports/projects/${projectId}/report`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to generate project report');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  getProjectInsights: async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/project-reports/projects/${projectId}/insights`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to get project insights');
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Logistics QR tracking (Super Admin)
  /** Issue driver track URL (JWT). Revokes any previous active token for this load. */
  issueLogisticsTokenAdmin: async (body = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/token/issue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getLogisticsAdminSummary: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/summary`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getLogisticsAdminConfig: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  putLogisticsAdminConfig: async (body = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getLogisticsDeliveries: async (params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
      });
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/deliveries?${search}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getLogisticsDeliveryDetail: async (loadId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/deliveries/${loadId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getLogisticsDeliveryPerformance: async (params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
      });
      const response = await fetch(
        `${API_BASE_URL}/api/logistics/admin/deliveries/performance?${search}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  getLogisticsAlerts: async (params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
      });
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/alerts?${search}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  resolveLogisticsAlert: async (alertId, notes = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/logistics/admin/alerts/${alertId}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ notes })
      });
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  postLogisticsManualComplete: async (loadId, body = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/logistics/admin/deliveries/${loadId}/manual-complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        }
      );
      return await handleApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  /** Delivery performance: on-time vs late, unloading minutes (max 5000 rows). */
  exportLogisticsDeliveryPerformanceCsv: async (params = {}) => {
    const token = localStorage.getItem('token');
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
    });
    const response = await fetch(
      `${API_BASE_URL}/api/logistics/admin/deliveries/performance-export?${search}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        msg = err.message || err.error || msg;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logistics-delivery-performance-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  /** Download CSV using current filter params (max 5000 rows on server). */
  exportLogisticsDeliveriesCsv: async (params = {}) => {
    const token = localStorage.getItem('token');
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') search.append(k, String(v));
    });
    const response = await fetch(`${API_BASE_URL}/api/logistics/admin/deliveries/export?${search}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        msg = err.message || err.error || msg;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logistics-deliveries-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }
};
